import type { TagTone } from '@dayon/design-system';
import { formatPesos, parsePesos } from '@dayon/money';

/**
 * The pure half of the provider-transactions screen: the row shape
 * `GET /api/games/provider-transactions` answers (SA-124), its vocabulary, and
 * the query it is asked with. Separate so it is testable without a DOM.
 */

export interface ProviderTransaction {
  id: string;
  providerCode: string;
  /** The provider's txnid. */
  externalId: string;
  /** PLACE_BET · PLAYER_WIN · PLAYER_LOST · BET_CANCEL · BAL_ADJUST */
  operation: string;
  /** Pesos, as a string; null when the callback carried no amount. */
  amount: string | null;
  /** APPLIED · REFUSED · NOOP · CANCELLED · PRE_CANCELLED, or null. */
  status: string | null;
  roundRef: string | null;
  gameCode: string | null;
  playerRef: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** How many times the provider delivered this txnid; above 1, the later ones posted nothing again. */
  attempts: number;
  lastAttemptAt: string | null;
}

export interface ProviderTransactionPage {
  items: ProviderTransaction[];
  nextCursor: string | null;
  /** True when the reader is restricted to some brands, and the rows are only theirs (SEC-002). */
  brandRestricted: boolean;
}

export const OPERATION_LABEL: Record<string, string> = {
  PLACE_BET: 'Bet',
  PLAYER_WIN: 'Win',
  PLAYER_LOST: 'Lost',
  BET_CANCEL: 'Bet cancelled',
  BAL_ADJUST: 'Balance adjustment',
  /* JILI's. One call carries a bet and its result; a table game is staked and settled as a hand. */
  BET: 'Bet and result',
  CANCEL_BET: 'Bet cancelled',
  SESSION_BET: 'Hand staked',
  SESSION_SETTLE: 'Hand settled',
  CANCEL_SESSION: 'Hand cancelled',
};

/** The providers whose callbacks the screen can list. */
export const TRANSACTION_PROVIDERS: readonly { code: string; label: string }[] = [
  { code: 'SA', label: 'SA Gaming' },
  { code: 'JILI', label: 'JILI' },
];

export const TXN_STATUS: Record<string, { label: string; tone: TagTone }> = {
  APPLIED: { label: 'Applied', tone: 'accent' },
  REFUSED: { label: 'Refused', tone: 'outline' },
  NOOP: { label: 'No change', tone: 'quiet' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  PRE_CANCELLED: { label: 'Cancelled before its bet', tone: 'neutral' },
};

/** A peso string as the platform writes money; the raw text if it is not one. */
export function amountText(amount: string | null): string {
  if (amount === null) return '—';
  try {
    return formatPesos(parsePesos(amount));
  } catch {
    return amount;
  }
}

/**
 * The query string for one page.
 *
 * The dates go as plain YYYY-MM-DD and the server reads them as MANILA
 * calendar days, inclusive — the same dialect the findings list uses. The
 * browser does not compute the offset: it used to send an ISO instant with
 * +08:00 stitched on, which is the same day only while both halves agree, and
 * a UTC-day filter on a Manila business day is off by eight hours.
 */
export function transactionsPath(filters: Filters, cursor: string | null): string {
  const q = new URLSearchParams({ provider: filters.provider, limit: '50' });
  if (filters.playerRef.trim()) q.set('playerRef', filters.playerRef.trim());
  if (filters.txnId.trim()) q.set('txnId', filters.txnId.trim());
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  if (cursor) q.set('cursor', cursor);
  return `/api/games/provider-transactions?${q.toString()}`;
}

export interface Filters {
  provider: string;
  playerRef: string;
  txnId: string;
  from: string;
  to: string;
}
